import { produce } from 'immer';
import _ from 'lodash';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useMount from '../../../../../common/hooks/useMount';
import Breadcrumbs from '../../../../../common/widgets/Breadcrumbs';
import Button from '../../../../../common/widgets/Button';
import Icons from '../../../../../common/widgets/Icons';
import Loader from '../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../common/widgets/LoadingSpinner';
import { CustomerInfoContext } from '../../../contexts/CustomerInfoContext';
import {
  AccountInfo,
  CustomerInvoicingAddressModel,
} from '../../../models/Customers';
import { resources } from '../../../resources/resources';
import { customerService } from '../../../services/customer';
import applicationCrumbs from '../../ApplicationCrumbs';
import { InvoicingAddressDialog } from '../../Common/Dialogs/InvoicingAddress';
import {
  InvoicingAddress,
  InvoicingAddressContextTypes,
} from '../../Common/Dialogs/InvoicingAddress/types';
import PlatformInformation from '../../Common/PlatformInformation';
import MyAccountMenu from '../Common/MyAccountMenu';
import { DeleteAccountDialog } from './DeleteAccountDialog';
import { EditAccountInfoDialog } from './EditAccountInfoDialog';
import { EditDeliveryAddressDialog } from './EditDeliveryAddressDialog';
import { formatAddressText } from './common';

export const AccountInfoPage = () => {
  const { t: translate } = useTranslation();
  const { customerInfo } = useContext(CustomerInfoContext);
  const crumbs = applicationCrumbs(translate);
  const [info, setInfo] = useState<AccountInfo | null>(null);
  const [isEditDialogVisible, setIsEditDialogVisible] = useState(false);
  const [isDeleteDialogVisible, setIsDeleteDialogVisible] = useState(false);
  const [
    isEditDeliveryAddressDialogVisible,
    setIsEditDeliveryAddressDialogVisible,
  ] = useState(false);
  const [
    isEditInvoicingAddressDialogVisible,
    setIsEditInvoicingAddressDialogVisible,
  ] = useState(false);
  const [showErrorPanel, setShowErrorPanel] = useState<boolean>(false);

  const loadAccountInfo = async (abortController?: AbortController) => {
    const r = await customerService.getAccountInfo({
      signal: abortController?.signal,
    });
    if (!r.hasErrors) {
      setInfo({
        ..._.omit(r.data, 'addressSelector', 'address1'),
        address: formatAddressText(
          r.data.addressSelector,
          r.data.address1 ?? '',
        ),
      });
    } else {
      if (!abortController?.signal.aborted) {
        setShowErrorPanel(true);
      }
    }
  };

  const closeDialog = (reloadData?: boolean) => {
    setIsEditDialogVisible(false);
    setIsEditDeliveryAddressDialogVisible(false);
    setIsEditInvoicingAddressDialogVisible(false);
    if (reloadData) {
      setInfo(null);
      loadAccountInfo();
    }
  };

  const getFullDeliveryAddress = () => {
    let arr = new Array(
      info?.county?.trim(),
      info?.city?.trim(),
      info?.deliveryAddressStreet?.trim() &&
        `${translate(
          resources.accountInfo.deliveryAddress.street,
        )} ${info?.deliveryAddressStreet?.trim()}`,
      info?.deliveryAddressNo?.trim() &&
        `${translate(
          resources.accountInfo.deliveryAddress.number,
        )} ${info?.deliveryAddressNo?.trim()}`,
      info?.deliveryAddressBloc?.trim() &&
        `${translate(
          resources.accountInfo.deliveryAddress.bloc,
        )} ${info?.deliveryAddressBloc?.trim()}`,
      info?.deliveryAddressEntrance?.trim() &&
        `${translate(
          resources.accountInfo.deliveryAddress.entrance,
        )} ${info?.deliveryAddressEntrance?.trim()}`,
      info?.deliveryAddressFloor?.trim() &&
        `${translate(
          resources.accountInfo.deliveryAddress.floor,
        )} ${info?.deliveryAddressFloor?.trim()}`,
      info?.deliveryAddressApartment?.trim() &&
        `${translate(
          resources.accountInfo.deliveryAddress.apartment,
        )} ${info?.deliveryAddressApartment?.trim()}`,
    );

    return arr.filter(a => !_.isEmpty(a)).join(', ');
  };

  useMount(loadAccountInfo);

  return (
    <>
      <Breadcrumbs
        entries={[crumbs.home, crumbs.accountDashboard, crumbs.accountInfo]}
      />
      <section className="section-main section-actions my-account-page">
        <div className="container">
          <div className="area-actions">
            <MyAccountMenu />
          </div>
          <div className="area-content">
            <PlatformInformation />
            <div className="panel padding-xl">
              <div className="page-actions">
                <h1>{translate(resources.menu.accountInfo)}</h1>
                {!!info && !customerInfo.isSavingsUser && (
                  <>
                    <div className="page-action">
                      <Button
                        onClick={() => setIsEditDialogVisible(true)}
                        className="btn btn-solid btn-sm btn-spacing"
                        icon={<Icons.Edit />}
                      >
                        <span className="btn-text">
                          {translate(resources.accountInfo.edit.button)}
                        </span>
                      </Button>
                      <Button
                        onClick={() => setIsDeleteDialogVisible(true)}
                        className="btn btn-outline btn-sm"
                        icon={<Icons.DeleteAccount />}
                      >
                        <span className="btn-text">
                          {translate(resources.accountInfo.delete.button)}
                        </span>
                      </Button>
                    </div>
                  </>
                )}
              </div>

              <Loader
                loading={!info}
                loader={<LoadingSpinner />}
                showErrorPanel={showErrorPanel}
              >
                {!!info && (
                  <>
                    <div className="row">
                      <div className="col col-6 col-sm-12">
                        <div className="control-group d-info">
                          <div className="control-content">
                            <label>{translate(resources.firstname)}</label>
                            <div className="d-value">{info.firstname}</div>
                          </div>
                        </div>
                      </div>
                      <div className="col col-6 col-sm-12">
                        <div className="control-group d-info">
                          <div className="control-content">
                            <label>{translate(resources.lastname)}</label>
                            <div className="d-value">{info.lastname}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col col-6 col-sm-12">
                        <div className="control-group d-info">
                          <div className="control-content">
                            <label>{translate(resources.email)}</label>
                            <div className="d-value">
                              {customerInfo.isSavingsUser
                                ? info.email.replace('gappusr_', '')
                                : info.email}
                            </div>
                          </div>
                        </div>
                      </div>
                      {!customerInfo.isSavingsUser && (
                        <div className="col col-6 col-sm-12">
                          <div className="control-group d-info">
                            <div className="control-content">
                              <label>{translate(resources.phoneNumber)}</label>
                              <div className="d-value">{info.telephone}</div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    {!customerInfo.isSavingsUser && (
                      <div className="row">
                        <div className="col col-6 col-sm-12">
                          <div className="control-group d-info">
                            <div className="control-content">
                              <label>
                                {translate(resources.companyAddress)}
                              </label>
                              <div className="d-value">{info.address}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {isEditDialogVisible && (
                      <EditAccountInfoDialog
                        closeDialog={closeDialog}
                        phoneNumber={info.telephone}
                        address={{ value: info.addressId, text: info.address }}
                      />
                    )}
                    {isDeleteDialogVisible && (
                      <DeleteAccountDialog
                        closeDialog={() => setIsDeleteDialogVisible(false)}
                      />
                    )}
                  </>
                )}
              </Loader>
            </div>
            {!!info && info.showPersonalAddress && (
              <div className="panel padding-xl">
                <div className="page-actions">
                  <h1>{translate(resources.deliveryAddress)}</h1>
                  {!!info && (
                    <div className="page-action">
                      <Button
                        onClick={() =>
                          setIsEditDeliveryAddressDialogVisible(true)
                        }
                        className="btn btn-solid btn-sm"
                        icon={<Icons.Edit />}
                      >
                        <span className="btn-text">
                          {info.hasPersonalAddress
                            ? translate(
                                resources.accountInfo.deliveryAddress
                                  .editButton,
                              )
                            : translate(
                                resources.accountInfo.deliveryAddress.addButton,
                              )}
                        </span>
                      </Button>
                    </div>
                  )}
                </div>

                <Loader
                  loading={!info}
                  loader={<LoadingSpinner />}
                  showErrorPanel={showErrorPanel}
                >
                  {!!info && (
                    <div className="delivery-address">
                      <div className="delivery-address-icon">
                        <div>
                          <Icons.AddressCar />
                        </div>
                      </div>
                      {info.hasPersonalAddress ? (
                        <div className="delivery-address-details">
                          {getFullDeliveryAddress()}
                        </div>
                      ) : (
                        <div className="delivery-address-placeholder">
                          {translate(
                            resources.accountInfo.deliveryAddress.placeholder,
                          )}
                        </div>
                      )}

                      {isEditDeliveryAddressDialogVisible && (
                        <EditDeliveryAddressDialog
                          closeDialog={closeDialog}
                          hasPersonalAddress={info.hasPersonalAddress}
                          city={info.city}
                          county={info.county}
                          deliveryAddressStreet={info.deliveryAddressStreet}
                          deliveryAddressNo={info.deliveryAddressNo}
                          deliveryAddressBloc={info.deliveryAddressBloc}
                          deliveryAddressEntrance={info.deliveryAddressEntrance}
                          deliveryAddressFloor={info.deliveryAddressFloor}
                          deliveryAddressApartment={
                            info.deliveryAddressApartment
                          }
                        />
                      )}
                    </div>
                  )}
                </Loader>
              </div>
            )}
            {!!info && (
              <div className="panel padding-xl">
                <div className="page-actions">
                  <h1>
                    {translate(resources.invoicingAddress.account.sectionTitle)}
                  </h1>
                  <div className="page-action">
                    <Button
                      onClick={() =>
                        setIsEditInvoicingAddressDialogVisible(true)
                      }
                      className="btn btn-solid btn-sm"
                      icon={<Icons.Edit />}
                    >
                      <span className="btn-text">
                        {info.customerInvoicingAddress
                          ? translate(
                              resources.invoicingAddress.account.editButton,
                            )
                          : translate(
                              resources.invoicingAddress.account.addButton,
                            )}
                      </span>
                    </Button>
                  </div>
                </div>

                <Loader
                  loading={!info}
                  loader={<LoadingSpinner />}
                  showErrorPanel={showErrorPanel}
                >
                  {!!info && (
                    <div className="delivery-address">
                      <div className="delivery-address-icon">
                        <div>
                          <Icons.Invoice />
                        </div>
                      </div>
                      {info.customerInvoicingAddress ? (
                        <div className="delivery-address-details">
                          <div>
                            {info.customerInvoicingAddress.firstName}{' '}
                            {info.customerInvoicingAddress.lastName}
                          </div>
                          <div>
                            {`${info.customerInvoicingAddress.county.text}, ${info.customerInvoicingAddress.city.text}, ${info.customerInvoicingAddress.addressLine}`}
                          </div>
                        </div>
                      ) : (
                        <div className="delivery-address-placeholder">
                          {translate(
                            resources.invoicingAddress.account.placeholder,
                          )}
                        </div>
                      )}

                      {isEditInvoicingAddressDialogVisible && (
                        <InvoicingAddressDialog
                          contextType={InvoicingAddressContextTypes.MyAccount}
                          closeDialog={newCustomerInvoicingAddress => {
                            setIsEditInvoicingAddressDialogVisible(false);
                            if (newCustomerInvoicingAddress) {
                              setInfo(
                                produce(d => {
                                  d!.customerInvoicingAddress =
                                    newCustomerInvoicingAddress;
                                }),
                              );
                            }
                          }}
                          initialData={getInvoicingAddressData(
                            info.customerInvoicingAddress,
                          )}
                        />
                      )}
                    </div>
                  )}
                </Loader>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

const getInvoicingAddressData = (
  customerInvoicingAddress?: CustomerInvoicingAddressModel,
): InvoicingAddress | undefined => {
  if (!customerInvoicingAddress) {
    return undefined;
  }
  return {
    firstName: customerInvoicingAddress.firstName,
    lastName: customerInvoicingAddress.lastName,
    county: customerInvoicingAddress.county,
    counties: customerInvoicingAddress.counties,
    city: customerInvoicingAddress.city,
    cities: customerInvoicingAddress.cities,
    addressLine: customerInvoicingAddress.addressLine,
    cnp: customerInvoicingAddress.cnp ?? undefined,
    agreeCnp: !!customerInvoicingAddress.cnp,
  };
};
